import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Ban,
  Check,
  Copy,
  KeyRound,
  LayoutGrid,
  MonitorSmartphone,
  Percent,
  UserPlus,
  UserRoundX,
  UserRoundCheck,
  Users,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { MemberStatus, TeamMemberSummary } from '@aeris/contracts'
import { ROLES, canActOnMember, canGrantRole } from '@aeris/permissions'
import { rateLabel } from '@aeris/printing'
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  InlineAlert,
  ListToolbar,
  Pagination,
  QueryState,
  ResultCount,
  RowActions,
  RowOpener,
  SearchInput,
  Select,
  SortableHeaderCell,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tooltip,
  VisuallyHidden,
} from '@aeris/ui'

import { CommissionRateDialog } from '../components/commission-rate-dialog.tsx'
import { TabHeader } from '../components/config-tabs.tsx'
import { MemberForm } from '../components/member-form.tsx'
import { MemberPagesDialog } from '../components/member-pages-dialog.tsx'
import { ROLE_LABELS } from '../components/role-labels.ts'
import { TeamMemberDialog } from '../components/team-member-dialog.tsx'
import { fetchCommissionRates } from '../lib/api/commissions.ts'
import {
  ApiError,
  changeMemberRole,
  fetchMemberSessions,
  fetchTeam,
  resetMemberPassword,
  revokeMemberSessions,
  setMemberEnabled,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { sessionOrigin } from '../lib/session-origin.ts'
import { useDebounced } from '../lib/use-debounced.ts'
import { sortKey, useTableSort } from '../lib/use-table-sort.ts'

/**
 * Team — who has access, and on what terms.
 *
 * The list screen pattern F007 established: server-side search, server-side
 * pagination, a page size the client cannot raise past the server's cap.
 *
 * What is specific to this screen is which controls a row offers. The rules are
 * the server's — nobody administers themselves, nobody acts on somebody
 * senior — and they are applied here with the same `@aeris/permissions`
 * functions the API refuses with. Hiding a button is not the enforcement; it is
 * what stops the interface from offering an action that would be refused.
 */

const PAGE_SIZE = 25

/**
 * "Há 3 dias", not a timestamp.
 *
 * Whether somebody still uses their access is a question about recency, and
 * `09/07/2026 14:32` makes the reader do the subtraction. `Intl.RelativeTimeFormat`
 * is the platform's own answer and carries the Portuguese.
 */
const relative = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' })

const lastSeenLabel = (iso: string | null): string => {
  if (iso === null) return 'Nunca entrou'

  const elapsedMs = Date.now() - new Date(iso).getTime()
  const minutes = Math.round(elapsedMs / 60_000)
  if (minutes < 1) return 'Agora'
  if (minutes < 60) return relative.format(-minutes, 'minute')

  const hours = Math.round(minutes / 60)
  if (hours < 24) return relative.format(-hours, 'hour')

  const days = Math.round(hours / 24)
  if (days < 30) return relative.format(-days, 'day')

  return relative.format(-Math.round(days / 30), 'month')
}

/** The live sessions of one person, with a way to end them. */
const SessionsDialog = ({
  member,
  onClose,
}: {
  member: TeamMemberSummary
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['team', 'sessions', member.userId],
    queryFn: ({ signal }) => fetchMemberSessions(member.userId, signal),
  })

  const revoke = useMutation({
    // O erro aparece na própria tela; o aviso geral não repete (F248).

    meta: { errorShownInline: true },
    mutationFn: async (sessionId?: string) =>
      await revokeMemberSessions(member.userId, sessionId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['team'] })
    },
  })

  const sessions = query.data?.sessions ?? []

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={`Sessões de ${member.displayName}`}
        description="Apenas as sessões desta conta. O acesso a outras contas não aparece aqui."
        className="max-w-2xl"
      >
        <div className="flex flex-col gap-3">
          {query.isPending ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : sessions.length === 0 ? (
            <EmptyState
              icon={
                <MonitorSmartphone aria-hidden="true" className="h-8 w-8" />
              }
              title="Nenhuma sessão ativa"
              description="Esta pessoa não está conectada nesta conta agora."
            />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Início</TableHeaderCell>
                    <TableHeaderCell>Última atividade</TableHeaderCell>
                    <TableHeaderCell>Origem</TableHeaderCell>
                    <TableHeaderCell className="text-right">
                      Ações
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow key={session.id}>
                      <TableCell className="whitespace-nowrap text-(--text-secondary)">
                        {new Date(session.createdAt).toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {lastSeenLabel(session.lastSeenAt)}
                        {session.isCurrent ? (
                          <Badge tone="info" className="ml-2">
                            Esta sessão
                          </Badge>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-(--text-secondary)">
                        {sessionOrigin(session)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          loading={
                            revoke.isPending && revoke.variables === session.id
                          }
                          onClick={() => {
                            revoke.mutate(session.id)
                          }}
                          leadingIcon={
                            <Ban aria-hidden="true" className="h-3.5 w-3.5" />
                          }
                        >
                          Encerrar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {revoke.isError ? (
            <InlineAlert tone="danger">
              Não foi possível encerrar. Tente de novo.
            </InlineAlert>
          ) : null}

          <div className="mt-2 flex justify-between gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={sessions.length === 0}
              loading={revoke.isPending && revoke.variables === undefined}
              onClick={() => {
                revoke.mutate(undefined)
              }}
              leadingIcon={<Ban aria-hidden="true" className="h-4 w-4" />}
            >
              Encerrar todas
            </Button>
            <Button type="button" variant="primary" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** The reset handover: the same one-time rule as adding somebody. */
const ResetDialog = ({
  name,
  password,
  onClose,
}: {
  name: string
  password: string
  onClose: () => void
}): ReactNode => {
  const [copied, setCopied] = useState(false)

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) return
      }}
    >
      <DialogContent
        title="Senha temporária"
        description="Anote agora: ela não será mostrada de novo."
        className="max-w-lg"
      >
        <div className="flex flex-col gap-4">
          <p
            role="alert"
            className="rounded-(--radius-md) border border-(--warning) bg-(--warning-subtle) px-3 py-2 text-(length:--text-xs) text-(--text-primary)"
          >
            As sessões de {name} foram encerradas. Entregue esta senha; o
            sistema vai pedir uma troca no próximo acesso.
          </p>
          <div className="flex items-center gap-2">
            <code
              data-testid="temporary-password"
              className="flex-1 select-all rounded-(--radius-md) border border-(--border-default) bg-(--surface-sunken) px-3 py-2 font-(--font-mono) text-(length:--text-sm) tracking-wider text-(--text-primary)"
            >
              {password}
            </code>
            {/*
              O mesmo Copiar de "Dar acesso" (F136): antes só a senha de quem
              entrava tinha o botão, e a de quem redefinia era copiada à mão.
              Melhor esforço — sem contexto seguro o navegador recusa, e a senha
              continua na tela para selecionar.
            */}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              leadingIcon={
                copied ? (
                  <Check aria-hidden="true" className="h-3.5 w-3.5" />
                ) : (
                  <Copy aria-hidden="true" className="h-3.5 w-3.5" />
                )
              }
              onClick={() => {
                try {
                  void navigator.clipboard.writeText(password).then(
                    () => {
                      setCopied(true)
                    },
                    () => {
                      setCopied(false)
                    }
                  )
                } catch {
                  setCopied(false)
                }
              }}
            >
              {copied ? 'Copiada' : 'Copiar'}
            </Button>
          </div>
          <DialogFooter>
            <Button type="button" variant="primary" onClick={onClose}>
              Já anotei
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export const EquipePage = (): ReactNode => {
  const { session, allows } = useAuth()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<MemberStatus | 'all'>('active')
  const [role, setRole] = useState<TeamMemberSummary['role'] | 'all'>('all')
  const [page, setPage] = useState(1)

  // Back to the first page: a new order at the old offset shows rows that
  // have nothing to do with the column that was clicked.
  const sort = useTableSort<'displayName' | 'email'>(
    'displayName',
    'asc',
    () => {
      setPage(1)
    }
  )
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sessionsFor, setSessionsFor] = useState<TeamMemberSummary | null>(null)
  const [pagesFor, setPagesFor] = useState<TeamMemberSummary | null>(null)
  /** A ficha de leitura (F154): clicar na linha, sem tocar em nada. */
  const [viewing, setViewing] = useState<TeamMemberSummary | null>(null)
  const [reset, setReset] = useState<{ name: string; password: string } | null>(
    null
  )

  const debouncedSearch = useDebounced(search)

  const actorRole = session?.context?.role ?? null
  const canWrite = allows('identity.user.write')
  const canRevoke = allows('identity.session.revoke')
  const canManageCommission = allows('commission.manage')

  /*
   * A % de comissão de cada pessoa (F137), para a coluna e para o diálogo. Só
   * para quem pode defini-la: a % do colega não é informação da equipe toda.
   */
  const commissionRates = useQuery({
    queryKey: ['commissions', 'rates'],
    queryFn: ({ signal }) => fetchCommissionRates(signal),
    enabled: canManageCommission,
  })
  const rateOf = (userId: string): string | null =>
    commissionRates.data?.sellers.find((row) => row.userId === userId)
      ?.currentRate ?? null
  const [commissionFor, setCommissionFor] = useState<TeamMemberSummary | null>(
    null
  )
  const rateLabelOf = (userId: string): string => {
    const rate = rateOf(userId)
    return rate === null ? '—' : rateLabel(rate)
  }

  const query = useQuery({
    queryKey: [
      'team',
      { search: debouncedSearch, status, role, page, ...sortKey(sort) },
    ],
    queryFn: ({ signal }) =>
      fetchTeam(
        {
          ...(debouncedSearch.trim().length > 0
            ? { search: debouncedSearch.trim() }
            : {}),
          ...(status === 'all' ? {} : { status }),
          ...(role === 'all' ? {} : { role }),
          page,
          pageSize: PAGE_SIZE,
          sort: sort.sort,
          direction: sort.direction,
        },
        signal
      ),
    placeholderData: (previous) => previous,
  })

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['team'] })
  }

  const report = (cause: unknown): void => {
    setError(
      cause instanceof ApiError
        ? cause.message
        : 'Não foi possível concluir. Tente novamente.'
    )
  }

  /*
   * Trocar o papel muda o que a pessoa pode fazer na hora seguinte: confirma
   * antes (F132). O `Select` gravava no clique, e um item errado promovia ou
   * rebaixava alguém sem volta visível.
   */
  const [pendingRole, setPendingRole] = useState<{
    member: TeamMemberSummary
    role: string
  } | null>(null)

  const roleMutation = useMutation({
    mutationFn: async (input: { member: TeamMemberSummary; role: string }) =>
      await changeMemberRole(input.member.userId, {
        version: input.member.version,
        role: input.role as 'owner',
      }),
    onSuccess: refresh,
    onError: report,
  })

  const statusMutation = useMutation({
    mutationFn: async (input: {
      member: TeamMemberSummary
      enabled: boolean
    }) =>
      await setMemberEnabled(
        input.member.userId,
        input.enabled,
        input.member.version
      ),
    onSuccess: refresh,
    onError: report,
  })

  const resetMutation = useMutation({
    mutationFn: async (member: TeamMemberSummary) =>
      await resetMemberPassword(member.userId),
    onSuccess: async (response) => {
      setReset({
        name: response.member.displayName,
        password: response.temporaryPassword,
      })
      await refresh()
    },
    onError: report,
  })

  const total = query.data?.total ?? 0
  const items = query.data?.items ?? []

  /**
   * Whether this row may be acted on at all.
   *
   * Two refusals, both the server's: you are not your own administrator, and
   * you do not act on somebody senior to you.
   */
  const actionable = (member: TeamMemberSummary): boolean =>
    actorRole !== null &&
    !member.isSelf &&
    canActOnMember(actorRole, member.role)

  const [revoking, setRevoking] = useState<TeamMemberSummary | null>(null)
  /*
   * Redefinir a senha desconecta a pessoa no meio do expediente: confirma
   * antes, como mudar o papel e remover o acesso já confirmavam (F136). Era o
   * único item do ⋯ que agia no clique.
   */
  const [resetting, setResetting] = useState<TeamMemberSummary | null>(null)

  /*
   * O que cada pessoa mostra, escrito uma vez: a tabela (tela larga) e os
   * cartões (celular, F136) desenham os mesmos selos, o mesmo papel e as mesmas
   * ações — duas cópias do JSX divergiriam na próxima regra de quem pode o quê.
   */
  const badgesFor = (member: TeamMemberSummary): ReactNode => (
    <>
      {member.isSelf ? (
        <Badge tone="info" className="ml-2">
          Você
        </Badge>
      ) : null}
      {member.status === 'disabled' ? (
        <Badge tone="neutral" className="ml-2">
          Sem acesso
        </Badge>
      ) : null}
      {member.mustChangePassword ? (
        <Tooltip content="Ainda usa a senha temporária">
          <Badge tone="warning" className="ml-2">
            Senha pendente
          </Badge>
        </Tooltip>
      ) : null}
    </>
  )

  /*
   * O papel, que nesta tela é um CONTROLE dentro da linha.
   *
   * O `stopPropagation` não é zelo: com `onOpen` na linha (F154), abrir este
   * select abriria a ficha por baixo do menu aberto. É o mesmo conserto que o
   * `RowActions` recebeu na F151, pela mesma razão — um clique que atravessa
   * o controle e chega na linha.
   */
  const roleFor = (member: TeamMemberSummary): ReactNode => (
    <span
      onClick={(event) => {
        event.stopPropagation()
      }}
    >
      {canWrite && actionable(member) ? (
        <Select
          value={member.role}
          aria-label={`Papel de ${member.displayName}`}
          className="w-44"
          onChange={(event) => {
            setError(null)
            setPendingRole({ member, role: event.target.value })
          }}
        >
          {ROLES.filter((role) =>
            // Only roles this person may hand out. The server
            // refuses the rest regardless.
            actorRole === null ? false : canGrantRole(actorRole, role)
          ).map((role) => (
            <option key={role} value={role}>
              {ROLE_LABELS[role]}
            </option>
          ))}
        </Select>
      ) : (
        <span className="text-(--text-secondary)">
          {ROLE_LABELS[member.role]}
        </span>
      )}
    </span>
  )

  const actionsFor = (member: TeamMemberSummary): ReactNode => (
    <>
      {canWrite && !actionable(member) ? (
        <span className="text-(length:--text-xs) text-(--text-muted)">
          {member.isSelf
            ? 'Seu acesso é administrado por outra pessoa'
            : 'Papel acima do seu'}
        </span>
      ) : null}
      <RowActions
        name={member.displayName}
        primary={
          canWrite && actionable(member) ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPagesFor(member)
              }}
              leadingIcon={
                <LayoutGrid aria-hidden="true" className="h-3.5 w-3.5" />
              }
            >
              Telas
            </Button>
          ) : undefined
        }
        items={[
          canManageCommission && {
            label: 'Comissão',
            icon: <Percent aria-hidden="true" className="h-4 w-4" />,
            onSelect: () => {
              setCommissionFor(member)
            },
          },
          canRevoke &&
            actionable(member) && {
              label: 'Sessões abertas',
              icon: (
                <MonitorSmartphone aria-hidden="true" className="h-4 w-4" />
              ),
              onSelect: () => {
                setSessionsFor(member)
              },
            },
          canWrite &&
            actionable(member) && {
              label: 'Redefinir senha',
              icon: <KeyRound aria-hidden="true" className="h-4 w-4" />,
              onSelect: () => {
                setError(null)
                setResetting(member)
              },
            },
          canWrite &&
            actionable(member) &&
            member.status !== 'active' && {
              label: 'Devolver acesso',
              icon: <UserRoundCheck aria-hidden="true" className="h-4 w-4" />,
              onSelect: () => {
                setError(null)
                statusMutation.mutate({ member, enabled: true })
              },
            },
          canWrite &&
            actionable(member) &&
            member.status === 'active' && {
              label: 'Remover acesso',
              danger: true,
              icon: <UserRoundX aria-hidden="true" className="h-4 w-4" />,
              onSelect: () => {
                setError(null)
                setRevoking(member)
              },
            },
        ]}
      />
    </>
  )

  return (
    <>
      <TabHeader
        title="Equipe"
        description="Quem tem acesso a esta conta e o que cada pessoa pode fazer."
        actions={
          canWrite ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setAdding(true)
              }}
              leadingIcon={<UserPlus aria-hidden="true" className="h-4 w-4" />}
            >
              Dar acesso
            </Button>
          ) : undefined
        }
      />

      <ListToolbar>
        <SearchInput
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            setPage(1)
          }}
          placeholder="Buscar por nome ou e-mail"
          aria-label="Buscar pessoas da equipe"
        />

        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as MemberStatus | 'all')
            setPage(1)
          }}
          aria-label="Situação"
          className="w-44"
        >
          <option value="active">Com acesso</option>
          <option value="disabled">Sem acesso</option>
          <option value="all">Todos</option>
        </Select>

        {/* "Quem são os caixas?" — o filtro que a loja pediu (F136). */}
        <Select
          value={role}
          onChange={(event) => {
            setRole(event.target.value as TeamMemberSummary['role'] | 'all')
            setPage(1)
          }}
          aria-label="Papel"
          className="w-44"
        >
          <option value="all">Todos os papéis</option>
          {ROLES.map((entry) => (
            <option key={entry} value={entry}>
              {ROLE_LABELS[entry]}
            </option>
          ))}
        </Select>
      </ListToolbar>

      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}

      <ResultCount
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        loading={query.isPending}
        none="Ninguém encontrado"
      />

      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
        what="a equipe"
        isEmpty={items.length === 0}
        filtered={debouncedSearch.trim().length > 0 || role !== 'all'}
        onClearFilters={() => {
          setSearch('')
          setRole('all')
          setPage(1)
        }}
        empty={
          <EmptyState
            icon={<Users aria-hidden="true" className="h-8 w-8" />}
            title="Ninguém com esta situação"
            description="Mude a situação para ver quem está com ou sem acesso."
          />
        }
      >
        <ul
          aria-label="Pessoas da equipe"
          className="flex flex-col gap-2 md:hidden"
        >
          {items.map((member) => (
            <li
              key={member.userId}
              className="flex flex-col gap-2 rounded-(--radius-md) border border-(--border-default) bg-(--surface) p-3"
            >
              <div className="flex items-start gap-2">
                <Avatar label={member.displayName} seed={member.userId} />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-(--weight-medium) text-(--text-primary)">
                    {member.displayName}
                  </span>
                  <span className="truncate text-(length:--text-xs) text-(--text-muted)">
                    {member.email}
                  </span>
                  <span className="mt-1 flex flex-wrap items-center">
                    {badgesFor(member)}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 text-(length:--text-sm)">
                {roleFor(member)}
                {canManageCommission ? (
                  <span className="text-(length:--text-xs) text-(--text-secondary)">
                    {`comissão ${rateLabelOf(member.userId)}`}
                  </span>
                ) : null}
                <span className="text-(length:--text-xs) text-(--text-muted)">
                  {lastSeenLabel(member.lastSeenAt)}
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {actionsFor(member)}
              </div>
            </li>
          ))}
        </ul>

        <TableContainer className="hidden md:block">
          <Table>
            <TableHead>
              <TableRow>
                <SortableHeaderCell {...sort.column('displayName')}>
                  Nome
                </SortableHeaderCell>
                <SortableHeaderCell {...sort.column('email')}>
                  E-mail
                </SortableHeaderCell>
                <TableHeaderCell>Papel</TableHeaderCell>
                {canManageCommission ? (
                  <TableHeaderCell numeric>Comissão</TableHeaderCell>
                ) : null}
                <TableHeaderCell>Último acesso</TableHeaderCell>
                <TableHeaderCell>
                  <VisuallyHidden>Ações</VisuallyHidden>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((member) => (
                <TableRow
                  key={member.userId}
                  onOpen={() => {
                    setViewing(member)
                  }}
                >
                  <TableCell>
                    <RowOpener
                      onOpen={() => {
                        setViewing(member)
                      }}
                    >
                      <span className="inline-flex items-center gap-2 align-middle">
                        <Avatar
                          label={member.displayName}
                          seed={member.userId}
                        />
                        <span className="text-(--text-primary)">
                          {member.displayName}
                        </span>
                      </span>
                    </RowOpener>
                    {badgesFor(member)}
                  </TableCell>
                  <TableCell className="text-(--text-secondary)">
                    {member.email}
                  </TableCell>
                  <TableCell>{roleFor(member)}</TableCell>
                  {canManageCommission ? (
                    <TableCell numeric>{rateLabelOf(member.userId)}</TableCell>
                  ) : null}
                  <TableCell className="whitespace-nowrap text-(--text-secondary)">
                    {lastSeenLabel(member.lastSeenAt)}
                  </TableCell>
                  <TableCell>
                    {/*
                      Telas à vista (F038), o resto no ⋯ (F132). Remover o
                      acesso vai por último e pede confirmação: tira a pessoa
                      do sistema no meio do expediente.
                    */}
                    {actionsFor(member)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      <ConfirmDialog
        open={pendingRole !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRole(null)
        }}
        title={`Mudar o papel de ${pendingRole?.member.displayName ?? ''}?`}
        description={
          pendingRole === null
            ? ''
            : `Passa de ${ROLE_LABELS[pendingRole.member.role]} para ${ROLE_LABELS[pendingRole.role as keyof typeof ROLE_LABELS]}. O que a pessoa pode ver e fazer muda no próximo clique dela.`
        }
        confirmLabel="Mudar papel"
        cancelLabel="Voltar"
        loading={roleMutation.isPending}
        onConfirm={() => {
          const pending = pendingRole
          setPendingRole(null)
          if (pending !== null) roleMutation.mutate(pending)
        }}
      />

      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(next) => {
          if (!next) setRevoking(null)
        }}
        title="Remover acesso"
        description={
          revoking === null
            ? ''
            : `Remover o acesso de ${revoking.displayName}? A pessoa é desconectada agora e não entra mais até alguém devolver o acesso. O que ela registrou continua no sistema.`
        }
        confirmLabel="Remover acesso"
        loading={statusMutation.isPending}
        onConfirm={() => {
          if (revoking === null) return
          statusMutation.mutate(
            { member: revoking, enabled: false },
            {
              onSettled: () => {
                setRevoking(null)
              },
            }
          )
        }}
      />

      <ConfirmDialog
        open={resetting !== null}
        onOpenChange={(next) => {
          if (!next) setResetting(null)
        }}
        title={`Redefinir a senha de ${resetting?.displayName ?? ''}?`}
        description="Gera uma senha temporária e encerra agora as sessões abertas dessa pessoa. Ela escolhe uma senha nova no próximo acesso."
        confirmLabel="Redefinir senha"
        cancelLabel="Voltar"
        loading={resetMutation.isPending}
        onConfirm={() => {
          if (resetting === null) return
          resetMutation.mutate(resetting, {
            onSettled: () => {
              setResetting(null)
            },
          })
        }}
      />

      {adding ? (
        <MemberForm
          onClose={() => {
            setAdding(false)
          }}
        />
      ) : null}

      {commissionFor === null ? null : (
        <CommissionRateDialog
          member={commissionFor}
          onClose={() => {
            setCommissionFor(null)
          }}
        />
      )}

      {viewing === null ? null : (
        <TeamMemberDialog
          member={viewing}
          {...(canManageCommission
            ? { commissionLabel: rateLabelOf(viewing.userId) }
            : {})}
          onClose={() => {
            setViewing(null)
          }}
          /* A mesma ação principal que a linha oferece, sob a mesma regra. */
          {...(canWrite && actionable(viewing)
            ? {
                onPages: () => {
                  setPagesFor(viewing)
                  setViewing(null)
                },
              }
            : {})}
        />
      )}

      {pagesFor === null ? null : (
        <MemberPagesDialog
          member={pagesFor}
          onClose={() => {
            setPagesFor(null)
          }}
        />
      )}

      {sessionsFor === null ? null : (
        <SessionsDialog
          member={sessionsFor}
          onClose={() => {
            setSessionsFor(null)
          }}
        />
      )}

      {reset === null ? null : (
        <ResetDialog
          name={reset.name}
          password={reset.password}
          onClose={() => {
            setReset(null)
          }}
        />
      )}
    </>
  )
}
